// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import {
  type BaseQueryFn, createApi, type FetchArgs, fetchBaseQuery, type FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import jwt from 'jwt-decode'; // import dependency
import JwtService from '../auth/jwt/jwtService';
import { handleLogout } from '../redux/authentication';
import { Message } from './notifications/Message';
import { Api } from '../api/ApiRoute';

const jwtService = new JwtService();

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_BASE_URL}v1/`,
  prepareHeaders: (headers) => {
    let accessToken = jwtService.getToken();
    headers.set('Authorization', `${jwtService.jwtConfig.tokenType} ${accessToken}`);

    if (accessToken) {
      headers.set('Authorization', `${jwtService.jwtConfig.tokenType} ${accessToken}`);
    } else {
      accessToken = jwtService.getMasterToken();
      if (accessToken) headers.set('Authorization', `${jwtService.jwtConfig.tokenType} ${accessToken}`);
    }
    return headers;
  },
  credentials: 'include',
  mode: 'cors',
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const accessToken = jwtService.getToken();
    const masterAccessToken = jwtService.getMasterToken();

    if (accessToken != null) {
      const token: any = jwt(accessToken);

      const validTo = token.exp;
      const currentTime = Date.now();

      if (validTo < (currentTime / 1000)) {
        const messageResult = await Message('You have been logged out due to your session expiring.', 'Please click Ok to return to the login page.', 'info');
        api.dispatch(handleLogout());

        if (messageResult) {
          window.location.reload();
        }

        return result;
      }
    } else if (masterAccessToken != null) {
      const token: any = jwt(masterAccessToken);

      const validTo = token.exp;
      const currentTime = Date.now();

      if (validTo < (currentTime / 1000)) {
        const messageResult = await Message('You have been logged out due to your session expiring.', 'Please click Ok to return to the login page.', 'info');

        api.dispatch(handleLogout());
        if (messageResult) {
          window.location.reload();
        }

        return result;
      }
    }

    const headers = new Headers();
    headers.set('Authorization', `${jwtService.jwtConfig.tokenType} ${accessToken}`);

    await fetch(`${process.env.REACT_APP_API_BASE_URL}v1` + Api.V1MasterUsersSettingsList.BaseRequest.url, {
      method: 'GET',
      headers: headers,
    })
      .then(async json => {
        if (json.status === 401) {
          const result = await Message('You have been logged out due to your session expiring.', 'Please click Ok to return to the login page.', 'info');
          api.dispatch(handleLogout());

          if (result) {
            window.location.reload();
          }
        }
      });
  }
  return result;
};
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace invalidationTags {
  export const TENANT = 'Tenant';
  export const USER = 'User';
  export const SSRS = 'Ssrs';
  export const IMPORT_FORM = 'ImportForm';
  export const TENANT_LOGO = 'TenantLogo';
  export const PERMISSIONS = 'Permissions';
  export const VIEW_GROUPS = 'ViewGroups';
  export const ROLE = 'Role';
  export const FOLDER = 'Folder';
  export const FILE = 'File';
  export const POWER_BI = 'PowerBi';
  export const TWO_STEP = 'TwoStep';
  export const MIGRATE_REPORT = 'MigrateReport';
  export const MAPPING = 'Mapping';
  export const MISSING_MAPPINGS = 'MissingMappings';
  export const RECURRING_LOG = 'RecurringLog';
  export const FORECAST = 'Forecast';
  export const EDITION = 'Edition';

  //BI HUB
  export const BIHUB_PIPELINE = 'BiHubPipeline';
  export const BIHUB_PIPELINE_LOGS = 'BiHubPipelineLogs';
  export const BIHUB_TENANT_STATUS = 'BiHubTenantStatus';
  export const BIHUB_TASK = 'BiHubTask';
}

const tagTypes: string[] = [
  invalidationTags.TENANT,
  invalidationTags.USER,
  invalidationTags.SSRS,
  invalidationTags.IMPORT_FORM,
  invalidationTags.TENANT_LOGO,
  invalidationTags.PERMISSIONS,
  invalidationTags.VIEW_GROUPS,
  invalidationTags.ROLE,
  invalidationTags.FOLDER,
  invalidationTags.FILE,
  invalidationTags.POWER_BI,
  invalidationTags.TWO_STEP,
  invalidationTags.MIGRATE_REPORT,
  invalidationTags.MAPPING,
  invalidationTags.MISSING_MAPPINGS,
  invalidationTags.RECURRING_LOG,
  invalidationTags.FORECAST,
  invalidationTags.EDITION,

  //BI HUB
  invalidationTags.BIHUB_PIPELINE,
  invalidationTags.BIHUB_TASK,
  invalidationTags.BIHUB_PIPELINE_LOGS,
  invalidationTags.BIHUB_TENANT_STATUS,
];

// These are the tags which can be invalidated from the server
export const availableForSignalRTagInvalidation = [
  invalidationTags.BIHUB_PIPELINE_LOGS,
  invalidationTags.BIHUB_TENANT_STATUS,
  invalidationTags.IMPORT_FORM,
  invalidationTags.RECURRING_LOG
];

export const emptySplitApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes,
});
